import type { LocaleNumberDisplay } from './numberUtils.types'

/**
 * Finds the closest divisor of the given denominator to the specified value.
 *
 * Iterates through all divisors of `denominator` and returns the one closest to `value`.
 *
 * @param value - The number to find the closest multiple to.
 * @param denominator - The number whose divisors are considered as possible candidates.
 * @returns The divisor of `denominator` that is closest to `value`.
 */
export function findClosestDivisor(value: number, denominator: number) {
  let snappedValue = value
  let minDiff = Infinity
  for (let i = 1; i <= denominator; i++) {
    if (denominator % i === 0) {
      const diff = Math.abs(i - value)
      if (diff < minDiff) {
        minDiff = diff
        snappedValue = i
      }
    }
  }
  return snappedValue
}

export function calcMean(values: number[]): number {
  return values.reduce((a, b) => a + b, 0) / values.length
}

const separatorsCache = new Map<string, { group: string; decimal: string }>()

function getSeparators(locale: string) {
  let separators = separatorsCache.get(locale)
  if (!separators) {
    const parts = new Intl.NumberFormat(locale).formatToParts(10000.1)
    separators = {
      group: parts.find((part) => part.type === 'group')?.value ?? ' ',
      decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    }
    separatorsCache.set(locale, separators)
  }
  return separators
}

export function getGroupSeparator(locale: string = window.locale ?? 'en-GB'): string {
  return getSeparators(locale).group
}

export function getDecimalSeparator(locale: string = window.locale ?? 'en-GB'): string {
  return getSeparators(locale).decimal
}

const NUMERIC_PATTERN = /^-?\d+(?:\.\d+)?$/
const LOCALE_NUMBER_PATTERN = /^-?\d+(?:\.\d*)?$/

/**
 * Formats a number or numeric string with the browser locale's thousand and decimal
 * separators (e.g. sv-SE: "1 234,5", en-GB: "1,234.5", de-DE: "1.234,5"). Works on the
 * string form to preserve trailing zeros and precision beyond Number. Non-numeric input
 * (e.g. "-") is returned as is.
 */
export function addLocaleSeparators(value: number | string): string {
  const input = typeof value === 'number' ? value.toString() : value
  if (!NUMERIC_PATTERN.test(input)) {
    return input
  }

  return getLocaleNumberDisplay(input)?.text ?? input
}

export function getLocaleNumberDisplay(value: string): LocaleNumberDisplay | null {
  if (!LOCALE_NUMBER_PATTERN.test(value)) {
    return null
  }

  const { group, decimal } = getSeparators(window.locale ?? 'en-GB')
  const dotIndex = value.indexOf('.')
  const integer = dotIndex === -1 ? value : value.slice(0, dotIndex)
  const firstDigitIndex = integer.startsWith('-') ? 1 : 0
  const display: LocaleNumberDisplay = { text: '', isGrouping: [] }

  function append(characters: string, isGrouping: boolean): void {
    display.text += characters
    display.isGrouping.push(...Array.from({ length: characters.length }, () => isGrouping))
  }

  for (let index = 0; index < integer.length; index += 1) {
    if (index > firstDigitIndex && (integer.length - index) % 3 === 0) {
      append(group, true)
    }
    append(integer.charAt(index), false)
  }
  if (dotIndex !== -1) {
    append(`${decimal}${value.slice(dotIndex + 1)}`, false)
  }

  return display
}
